import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import Helpers from "@utils/Helpers";
import { IUserAddress, IUserInfo } from "@utils/Interfaces";
import notificationBell from "@assets/mp3/notification-sound.mp3";
import userApi from "services/UserApi";
import { conversationApi } from "services/ConversationApi";
import { errorLog } from "logging";

// DEFINE CONTEXT TYPE
interface UserContextType {
  userProfile: IUserInfo | null;
  updateUserProfile: (access: string, refresh: string) => void;
  isNewProductCreated: boolean;
  setIsNewProductState: (isNew: boolean) => void;
  playNoti: () => void;
  isOpenChatbox: boolean;
  setIsOpenChatbox: (is: boolean) => void;
  selectedReceiver: any;
  setSelectedReceiver: (value: any) => void;
  fetchAllAddress: () => void;
  userAddresses: any;
  updateUserAddress: (addresses: any[]) => void;
  messageCount: number;
  setMessageCount: (count: number) => void;
  setFetchCount: (is: boolean) => void;
  isDeveloperMode: boolean;
  setIsDeveloperMode: (is: boolean) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [userProfile, setUserProfile] = useState<IUserInfo>(
    Helpers.getUserInfo()
  );
  const [userAddresses, setUserAddresses] = useState<IUserAddress[]>(
    Helpers.getUserAddress()
  );
  const [messageCount, setMessageCount] = useState(0);
  const [fetchCount, setFetchCount] = useState(false);
  const [isNewProductCreated, setIsNewProductCreated] = useState(false);
  const [isOpenChatbox, setIsOpenChatbox] = useState(false);
  const [isDeveloperMode, setIsDeveloperMode] = useState(false);
  const [selectedReceiver, setSelectedReceiver] = useState({
    receiverId: null,
    conversationId: null,
    receiverAvatar: null,
    receiverName: null,
    referenceProduct: null,
  });

  const updateUserProfile = (access: string, refresh: string) => {
    const userInfo = Helpers.decodeToken(access);
    Helpers.storeAccessToken(access);
    Helpers.storeRefreshToken(refresh);
    Helpers.storeUserInfo(userInfo.user);
    setUserProfile(userInfo.user);
  };

  const setIsNewProductState = (isNew: boolean) => {
    setIsNewProductCreated(isNew);
  };

  const playNoti = async () => {
    try {
      const AudioContext = window.AudioContext;
      const audioContext = new AudioContext();
      const response = await fetch(notificationBell);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      source.start();
    } catch (err) {
      console.log("Audio playback error:", err);
    }
  };

  const fetchAllAddress = async () => {
    try {
      const response = await userApi.getAllAddress();
      if (response.status === 200) {
        Helpers.storeUserAddress(response.data.data);
        setUserAddresses(response.data.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const updateUserAddress = (addresses: any) => {
    Helpers.storeUserAddress(addresses);
    setUserAddresses(addresses);
  };

  useEffect(() => {
    if (fetchCount) {
      fetchMessageCount();
      setFetchCount(false);
    }
  }, [fetchCount]);

  const fetchMessageCount = async () => {
    try {
      const response = await conversationApi.countUnviewed();
      if (response.status === 200) {
        setMessageCount(response.data.data.unviewed_count);
      }
    } catch (err) {
      Helpers.notification.error("Cannot fetch message count");
      errorLog("UserContext.tsx - fetchMessageCount", err);
    }
  };

  return (
    <UserContext.Provider
      value={{
        userProfile,
        updateUserProfile,
        setIsNewProductState,
        isNewProductCreated,
        playNoti,
        isOpenChatbox,
        setIsOpenChatbox,
        selectedReceiver,
        setSelectedReceiver,
        fetchAllAddress,
        userAddresses,
        updateUserAddress,
        messageCount,
        setMessageCount,
        setFetchCount,
        isDeveloperMode,
        setIsDeveloperMode,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
